import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import { useSaveFlow } from "../../components/confirm";
import { confirmDiscardIfUnsaved, useUnsavedGuard } from "../../hooks/useUnsavedGuard";
import "./InstrumentPickerDialog.css";

interface InstrumentItem {
  oid: string;
  instrumentId: string | null;
  instrumentName: string | null;
  instrumentNumber: string | null;
}

/** Two-panel instrument picker: Available (left) ↔ Linked (right).
 *  Opened by clicking the "(n) Instruments" cell in set-instruments-field,
 *  set-instruments-lab, and set-instruments-sampleprep. */
export function InstrumentPickerDialog({
  testMethodOid,
  testCode,
  testName,
  linkTable,
  onClose,
  onSaved,
}: {
  testMethodOid: string;
  testCode: string;
  testName: string;
  /** "field" | "lab" | "sampleprep" */
  linkTable: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();

  // All instruments from the server split into available + linked pools
  const [allItems, setAllItems] = useState<InstrumentItem[]>([]);
  // Oids currently in the "Linked" panel (mutable during the session)
  const [linkedOids, setLinkedOids] = useState<Set<string>>(new Set());
  // Original linked set — used to detect unsaved changes
  const [originalLinked, setOriginalLinked] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [availSearch, setAvailSearch] = useState("");
  const [linkedSearch, setLinkedSearch] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get<InstrumentItem[]>(`/api/v1/test-method-instruments/${testMethodOid}/linked?linkTable=${linkTable}`),
      api.get<InstrumentItem[]>(`/api/v1/test-method-instruments/${testMethodOid}/available?linkTable=${linkTable}`),
    ])
      .then(([linkedRes, availRes]) => {
        if (!alive) return;
        const linked = linkedRes.data ?? [];
        const avail = availRes.data ?? [];
        const combined = [...linked, ...avail];
        setAllItems(combined);
        const ids = new Set<string>(linked.map((r) => r.oid));
        setLinkedOids(new Set(ids));
        setOriginalLinked(new Set(ids));
      })
      .catch(() => {
        if (alive) notify.error("Could not load instruments.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [testMethodOid, linkTable, notify]);

  const dirty = !setsEqual(linkedOids, originalLinked);
  useUnsavedGuard(dirty);

  const close = () => {
    void confirmDiscardIfUnsaved().then((leave) => {
      if (leave) onClose();
    });
  };

  const link = (oid: string) => setLinkedOids((s) => new Set([...s, oid]));
  const unlink = (oid: string) =>
    setLinkedOids((s) => {
      const n = new Set(s);
      n.delete(oid);
      return n;
    });

  const availItems = useMemo(
    () =>
      filterBySearch(
        allItems.filter((r) => !linkedOids.has(r.oid)),
        availSearch,
      ),
    [allItems, linkedOids, availSearch],
  );

  const linkedItems = useMemo(
    () =>
      filterBySearch(
        allItems.filter((r) => linkedOids.has(r.oid)),
        linkedSearch,
      ),
    [allItems, linkedOids, linkedSearch],
  );

  const save = () => {
    if (!dirty) {
      onClose();
      return;
    }
    void runSave({
      formName: "Instrument",
      auditAction: "edit",
      save: async () => {
        await api.put(`/api/v1/test-method-instruments/${testMethodOid}`, {
          linkedOids: [...linkedOids],
          linkTable,
          testCode,
        });
      },
      onSaved,
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
        notify.error(msg ?? "Could not save instrument assignments.");
      },
    });
  };

  const title = [testCode, testName].filter((s) => s.trim() !== "").join(" · ");

  return (
    <AppDialog
      open
      title={`Instruments — ${title}`}
      width={900}
      className="dlg-center"
      maximizable
      onClose={close}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={save}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={close}>
            Cancel
          </button>
        </>
      }
    >
      {loading ? (
        <div className="ipd-loading">Loading instruments…</div>
      ) : (
        <div className="ipd-panels">
          {/* Available panel */}
          <div className="ipd-panel">
            <div className="ipd-panel-header">
              <span className="ipd-panel-title">Available</span>
              <input
                className="ipd-search"
                placeholder="Search…"
                value={availSearch}
                onChange={(e) => setAvailSearch(e.target.value)}
                data-hotkeys-ignore
              />
            </div>
            <div className="ipd-grid-wrap">
              <table className="ipd-tbl">
                <thead>
                  <tr>
                    <th className="ipd-th-id">Instrument ID</th>
                    <th className="ipd-th-name">Instrument Name</th>
                    <th className="ipd-th-num">Instrument Number</th>
                    <th className="ipd-th-btn" />
                  </tr>
                </thead>
                <tbody>
                  {availItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="ipd-empty">
                        No available instruments
                      </td>
                    </tr>
                  ) : (
                    availItems.map((r) => (
                      <tr key={r.oid} className="ipd-row" onDoubleClick={() => link(r.oid)}>
                        <td>{r.instrumentId ?? ""}</td>
                        <td>{r.instrumentName ?? ""}</td>
                        <td>{r.instrumentNumber ?? ""}</td>
                        <td>
                          <button
                            type="button"
                            className="ipd-btn ipd-btn-add"
                            title="Add to linked"
                            onClick={() => link(r.oid)}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ipd-divider" />

          {/* Linked panel */}
          <div className="ipd-panel">
            <div className="ipd-panel-header">
              <span className="ipd-panel-title">Linked ({linkedItems.length})</span>
              <input
                className="ipd-search"
                placeholder="Search…"
                value={linkedSearch}
                onChange={(e) => setLinkedSearch(e.target.value)}
                data-hotkeys-ignore
              />
            </div>
            <div className="ipd-grid-wrap">
              <table className="ipd-tbl">
                <thead>
                  <tr>
                    <th className="ipd-th-id">Instrument ID</th>
                    <th className="ipd-th-name">Instrument Name</th>
                    <th className="ipd-th-num">Instrument Number</th>
                    <th className="ipd-th-btn" />
                  </tr>
                </thead>
                <tbody>
                  {linkedItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="ipd-empty">
                        No linked instruments
                      </td>
                    </tr>
                  ) : (
                    linkedItems.map((r) => (
                      <tr key={r.oid} className="ipd-row" onDoubleClick={() => unlink(r.oid)}>
                        <td>{r.instrumentId ?? ""}</td>
                        <td>{r.instrumentName ?? ""}</td>
                        <td>{r.instrumentNumber ?? ""}</td>
                        <td>
                          <button
                            type="button"
                            className="ipd-btn ipd-btn-remove"
                            title="Remove from linked"
                            onClick={() => unlink(r.oid)}
                          >
                            −
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AppDialog>
  );
}

function setsEqual(a: Set<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function filterBySearch(items: InstrumentItem[], q: string): InstrumentItem[] {
  if (!q.trim()) return items;
  const lq = q.toLowerCase();
  return items.filter(
    (r) =>
      (r.instrumentId ?? "").toLowerCase().includes(lq) ||
      (r.instrumentName ?? "").toLowerCase().includes(lq) ||
      (r.instrumentNumber ?? "").toLowerCase().includes(lq),
  );
}
